import {
  isSpanishMonthInFuture,
  isSpanishMonthStrictlyInFuture,
  parseSpanishMonthKey,
  SPANISH_MONTH_DISPLAY_MAP,
  SPANISH_MONTH_NAMES,
} from 'utils/dateHelpers';

/**
 * Filter and sort months by year and optionally by future status.
 * When skipFutureCheck is true (e.g. for n8n live data), all months for the year are kept.
 */
export function filterAndSortMonths(
  months: string[],
  year: number,
  strictFutureCheck = false,
  skipFutureCheck = false
): string[] {
  const yearSuffix = String(year).slice(-2);
  return months
    .filter((month) => {
      const monthYear = month.split('-')[1];
      const yearMatches = !monthYear || monthYear === yearSuffix;
      if (skipFutureCheck) {
        return yearMatches;
      }
      const notInFuture = strictFutureCheck ? !isSpanishMonthStrictlyInFuture(month) : !isSpanishMonthInFuture(month);
      return yearMatches && notInFuture;
    })
    .sort((a, b) => parseSpanishMonthKey(a) - parseSpanishMonthKey(b));
}

/**
 * Get months with data from industry data
 */
export function getMonthsWithData(
  industryData: Record<string, Record<string, number>> | undefined,
  sortedMonths: string[]
): string[] {
  const monthsWithData = new Set<string>();
  if (industryData) {
    Object.values(industryData).forEach((monthlyData) => {
      sortedMonths.forEach((month) => {
        const value = monthlyData[month];
        if (typeof value === 'number' && value > 0) {
          monthsWithData.add(month);
        }
      });
    });
  }
  return Array.from(monthsWithData).sort((a, b) => parseSpanishMonthKey(a) - parseSpanishMonthKey(b));
}

/**
 * Transform industry data for line charts
 */
export interface IndustryChartData {
  industry: string;
  data: number[];
  months: string[];
}

export function transformIndustryDataForChart(
  industryData: Record<string, Record<string, number>> | undefined,
  sortedMonthsWithData: string[],
  excludeTotal = true
): IndustryChartData[] {
  if (!industryData) {
    return [];
  }

  return Object.entries(industryData)
    .filter(([industry]) => !excludeTotal || !industry.toLowerCase().includes('total'))
    .map(([industry, monthlyData]) => ({
      industry,
      data: sortedMonthsWithData.map((month) => {
        const value = monthlyData[month];
        return typeof value === 'number' ? value : 0;
      }),
      months: sortedMonthsWithData,
    }));
}

/**
 * Calculate total line data from industry chart data
 */
export function calculateTotalLineData(
  industryChartData: IndustryChartData[],
  sortedMonthsWithData: string[]
): IndustryChartData | null {
  if (industryChartData.length === 0 || sortedMonthsWithData.length === 0) {
    return null;
  }

  return {
    industry: 'Total',
    data: sortedMonthsWithData.map((month) => {
      const total = industryChartData.reduce((sum, industryData) => {
        const monthIndex = industryData.months.indexOf(month);
        return sum + (monthIndex >= 0 ? industryData.data[monthIndex] : 0);
      }, 0);
      return total;
    }),
    months: sortedMonthsWithData,
  };
}

/**
 * Format month labels for display
 */
export function formatMonthLabels(months: string[]): string[] {
  return months.map((month) => {
    const monthName = month.split('-')[0];
    return SPANISH_MONTH_DISPLAY_MAP[monthName.toLowerCase()] || monthName;
  });
}

/**
 * Transform monthly revenue data for bar charts
 */
export interface MonthlyBarChartData {
  label: string;
  value: number;
}

export function transformMonthlyRevenueForBarChart(
  monthlyRevenue: Record<string, number> | undefined,
  year: number
): MonthlyBarChartData[] {
  if (!monthlyRevenue) {
    return [];
  }

  const yearSuffix = String(year).slice(-2);
  return Object.entries(monthlyRevenue)
    .filter(([key, value]) => {
      const monthYear = key.split('-')[1];
      const yearMatches = !monthYear || monthYear === yearSuffix;
      const hasData = typeof value === 'number' && value > 0;
      return yearMatches && hasData;
    })
    .map(([label, value]) => ({
      label: label.includes('-') ? label.split('-')[0] : label,
      value: typeof value === 'number' ? value : 0,
    }))
    .sort((a, b) => {
      const monthIndexA = SPANISH_MONTH_NAMES.findIndex((m) => m.toLowerCase().startsWith(a.label.toLowerCase()));
      const monthIndexB = SPANISH_MONTH_NAMES.findIndex((m) => m.toLowerCase().startsWith(b.label.toLowerCase()));
      return (monthIndexA === -1 ? 999 : monthIndexA) - (monthIndexB === -1 ? 999 : monthIndexB);
    });
}
